import React, {
  FC,
  memo,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  DragIndicator,
  FormatListBulleted,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { IconDelete, IconInputError } from "@app/components/icons";
import { DialogTypes, TYPE_FILE_UPLOAD, ResultType } from "@app/constants";
import { ConfirmDialog, IconMessage } from "@app/pages/Features";
import { REGEX, safeSliceGraphemes } from "@app/helpers";
import { useDrag, useDrop } from "react-dnd";
import {
  IconMaintenaceNewSize,
  IconNotApplicableDash,
} from "@app/components/icons/IconMaintenace/IconMaintenanceNew";
import { IconSelectedNewSize } from "@app/components/icons/IconSelected/IconSelectedNew";
import { ImageIcon } from "lucide-react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import "@app/pages/Features/GroupList/GroupDetail/Components/InspectionForm/Card.css";
import { MediaUpload } from "../Components/InspectionForm/MediaUpload/MediaUpload";
import {
  PredefinedCommentsDialog,
  MAX_PREDEFINED_COMMENTS,
  type PredefinedCommentOption,
} from "../Components/InspectionForm/PredefinedCommentsDialog";

const ITEM_TYPE = "CARD";

interface DragItem {
  inspectionItemId: number;
  index: number;
}

interface UpdateFormCardUIProps {
  id: string;
  index: number;
  moveCard: (dragIndex: number, hoverIndex: number) => void;
  inspectionItemName: string;
  inspectionItemNameErrors: any;
  inspectionItemDesErrors: any;
  prevCardName: string;
  prevCardDescription: string;
  errors: any;
  onUpdateItemName: (name: string) => void;
  onUpdateItemDescription: (description: string) => void;
  mediaFiles: File[];
  onMediaUpload: (files: File[]) => void;
  inspectionItemId: string;
  setMediaStates: React.Dispatch<React.SetStateAction<Record<string, File[]>>>;
  removedMediaStates: React.Dispatch<
    React.SetStateAction<Record<string, string[]>>
  >;
  isOpen: any;
  onToggle: any;
  onDeleteCard: (id: string) => void;
  onValidationChange: (isValid: boolean, index: number) => void;
  onValidationChangeDesc: (isValid: boolean, index: number) => void;
  showDragIcon: Boolean;
  focusOnName: boolean;
  onResultTypeChange: (value: string, index: number) => void;
  resultType: string;
  itemOptions?: PredefinedCommentOption[];
  onOptionsChange: (options: PredefinedCommentOption[], index: number) => void;
}

const switchToResultType: Record<number, string> = {
  1: ResultType.OK_OR_ANOMARY,
  2: ResultType.OK_ANOMARY_NOT_APPLICABLE,
  3: ResultType.NONE,
  4: ResultType.FREE_TEXT,
};

const resultTypeToSwitch: Record<string, number> = {
  [ResultType.OK_OR_ANOMARY]: 1,
  [ResultType.OK_ANOMARY_NOT_APPLICABLE]: 2,
  [ResultType.NONE]: 3,
  [ResultType.FREE_TEXT]: 4,
};

export const UpdateFormCardUI: FC<UpdateFormCardUIProps> = memo(
  function UpdateCardUI({
    inspectionItemName,
    inspectionItemNameErrors,
    inspectionItemDesErrors,
    prevCardName,
    prevCardDescription,
    onUpdateItemName,
    onUpdateItemDescription,
    mediaFiles,
    onMediaUpload,
    inspectionItemId,
    removedMediaStates,
    onDeleteCard,
    onValidationChange,
    index,
    moveCard,
    onValidationChangeDesc,
    showDragIcon = true,
    isOpen: initialIsOpen,
    onToggle,
    focusOnName,
    onResultTypeChange,
    resultType,
    itemOptions = [],
    onOptionsChange,
  }) {
    const { t } = useTranslation();

    const predefinedCommentCount = itemOptions?.length ?? 0;
    const hasPredefinedComments = predefinedCommentCount > 0;

    const answerOptions = [
      {
        id: 1,

        icons: [
          <IconMaintenaceNewSize fillColor={"#DA9309"} iconColor={"white"} />,
          <IconSelectedNewSize fillColor={"#0e7c47"} iconColor={"white"} />,
        ],
        label: t("ANOMARY"),
      },
      {
        id: 2,

        icons: [
          <IconNotApplicableDash />,
          <IconMaintenaceNewSize fillColor={"#DA9309"} iconColor={"white"} />,
          <IconSelectedNewSize fillColor={"#0e7c47"} iconColor={"white"} />,
        ],
        label: t("ANOMARYNOTAPP"),
      },
      {
        id: 3,

        icons: [<IconMessage />, <ImageIcon />],
        label: t("CommentFile"),
      },
    ];

    const confirmProps = {
      typeDialog: DialogTypes.ERROR,
      title: "titleDeleteCard",
      content: "desDeleteCard",
      btnSubmitText: "formAction.delete",
      onCancel() {
        setIsOpenDeleteDialog(false);
      },
      onSubmit() {
        onDeleteCard(inspectionItemId);

        setIsOpenDeleteDialog(false);
      },
      classBtnSubmit: "btn-primary bg-red",
    };

    const uploadDescription = [
      `${t("dialogReport.pleaseUploadFile")}`,
      `<span class="fs-12">${t("dialogReport.thatCanBeUploadedAre")}</span>`,
      `<span class="fs-12">${t("dialogReport.fileFormats")}</span>`,
      `<span class="fs-12">${t("dialogReport.videosCanBeUploaded")}</span>`,
    ];

    const styles = {
      cardItem: {
        marginBottom: "0px",
        paddingTop: "16px",
        paddingBottom: "10px",
        borderBottom: "1.5px solid #eee",
        paddingLeft: "16px",
        paddingRight: "16px",
      },
      cardTitle: {
        marginBottom: "8px",
      },
      textFieldWrapper: {
        marginTop: "0px",
      },
      arrowIcon: {
        color: "#ccc",
        fontSize: "24px",
      },
      text: {
        fontSize: "16px",
      },
      buttonGroup: {
        display: "flex",
        flexDirection: "column" as "column",
        alignItems: "center",
        gap: "8px",
      },
      button: {
        backgroundColor: "transparent",
        border: "none",
        cursor: "pointer",
        padding: "4px",
      },
      disabledButton: {
        opacity: 0.5,
        cursor: "not-allowed",
      },
      cardContainer: {
        borderRadius: "0px",
        padding: "16px",
        backgroundColor: "#f5f6f9",
        maxWidth: "100%",
        overflow: "hidden",
        borderBottom: "1px solid #eeeeee",
      },
    };

    const [localName, setLocalName] = useState(inspectionItemName);
    const [nameError, setNameError] = useState<string | null>(null);
    const [isOpenDeleteDialog, setIsOpenDeleteDialog] = React.useState(false);
    const [openPredefinedDialog, setOpenPredefinedDialog] = useState(false);
    const [descriptionError, setDescriptionError] = useState<string | null>(
      null,
    );
    const [localMediaFiles, setLocalMediaFiles] = useState<File[]>(
      mediaFiles || [],
    );
    const [refreshKey] = useState(0);
    const [, setFileCount] = useState(0);
    const ref = useRef<HTMLDivElement>(null);
    const dragIconRef = useRef<HTMLDivElement>(null);
    const [activeSwitch, setActiveSwitch] = useState(
      resultTypeToSwitch[resultType] || 1,
    );
    const [isHovered, setIsHovered] = useState(false);
    const nameInputRef = useRef<HTMLInputElement>(null);
    const [isOpen, setIsOpen] = useState(initialIsOpen);

    const [{ handlerId }, drop] = useDrop<
      DragItem,
      void,
      { handlerId: string | symbol | null }
    >({
      accept: ITEM_TYPE,
      collect: (monitor) => ({
        handlerId: monitor.getHandlerId(),
      }),
      hover(item: DragItem, monitor) {
        if (!ref.current) return;

        const dragIndex = item.index;
        const hoverIndex = index;

        if (dragIndex === hoverIndex) return;

        const hoverBoundingRect = ref.current?.getBoundingClientRect();
        const hoverMiddleY =
          (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

        const clientOffset = monitor.getClientOffset();
        const hoverClientY = clientOffset!.y - hoverBoundingRect.top;

        if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
        if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

        moveCard(dragIndex, hoverIndex);
        item.index = hoverIndex;
      },
    });

    const [{ isDragging }, drag] = useDrag({
      type: ITEM_TYPE,
      item: { inspectionItemId, index },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    });

    drag(dragIconRef);
    drop(ref);

    const toggleCard = useCallback(() => {
      setIsOpen((prev: any) => !prev);
      onToggle();
    }, [onToggle]);

    const handleNameChange = (value: string) => {
      setLocalName(value);

      const isNonEmpty = value.trim() !== "";
      const isValid =
        isNonEmpty &&
        REGEX.commonNameFieldRegex.test(value) &&
        REGEX.emojiRegex.test(value);

      if (isValid) {
        setNameError(null);
        onUpdateItemName(value);
        onValidationChange(true, index);
      } else if (!isNonEmpty) {
        setNameError(t("messages.require"));
        onValidationChange(false, index);
      } else if (!REGEX.commonNameFieldRegex.test(value)) {
        setNameError(t("invalidCharacters"));
        onValidationChange(false, index);
      } else if (!REGEX.emojiRegex.test(value)) {
        setNameError(t("emojisarenotallowed"));
        onValidationChange(false, index);
      }
    };

    const handleNameBlur = (e: { target: { blur: () => void } }) => {
      const trimmedName = localName.trim();

      if (trimmedName === "") {
        setNameError(t("messages.require"));
        onUpdateItemName("");
        onValidationChange(false, index);
        e.target.blur();
      } else if (!REGEX.commonNameFieldRegex.test(trimmedName)) {
        setNameError(t("invalidCharacters"));
        onUpdateItemName("");
        onValidationChange(false, index);
        e.target.blur();
      } else if (!REGEX.emojiRegex.test(trimmedName)) {
        setNameError(t("emojisarenotallowed"));
        onUpdateItemName("");
        onValidationChange(false, index);
        e.target.blur();
      } else {
        setNameError(null);
        onUpdateItemName(trimmedName);
        onValidationChange(true, index);
      }
    };

    const handleMediaUpload = (files: File[]) => {
      const existingFiles = localMediaFiles.map((file: any) => file.filePath);
      const newFiles = files.map((file: any) => file.filePath);

      const removedFiles = existingFiles.filter(
        (filePath: string) => !newFiles.includes(filePath),
      );

      if (removedFiles.length > 0) {
        removedMediaStates((prevState) => ({
          ...prevState,
          [inspectionItemId]: [
            ...(prevState[inspectionItemId] || []),
            ...removedFiles,
          ],
        }));
      }

      setLocalMediaFiles(files);
      onMediaUpload(files);
    };

    const handleDescriptionChange = (value: string) => {
      const isValid =
        value.trim() === "" ||
        (REGEX.commonNameFieldCharacterMuiltiLineRegex.test(value) &&
          REGEX.emojiRegex.test(value));

      if (!isValid) {
        setDescriptionError(t("invalidCharacters"));
        onValidationChangeDesc(false, index);
      } else {
        setDescriptionError(null);
        onValidationChangeDesc(true, index);
      }

      onUpdateItemDescription(value);
    };

    const handleDescriptionBlur = (e: { target: { blur: () => void } }) => {
      const trimmedDescription = prevCardDescription.trim();

      const isValid =
        trimmedDescription === "" ||
        (REGEX.commonNameFieldCharacterMuiltiLineRegex.test(
          trimmedDescription,
        ) &&
          REGEX.emojiRegex.test(trimmedDescription));

      if (!isValid) {
        setDescriptionError(t("invalidCharacters"));
        onValidationChangeDesc(false, index);
      } else {
        setDescriptionError(null);
        onValidationChangeDesc(true, index);
      }

      e.target.blur();
    };

    const handleSwitchChange = (switchNumber: number) => {
      setActiveSwitch(switchNumber);
      onResultTypeChange(
        switchToResultType[switchNumber as keyof typeof switchToResultType],
        index,
      );
    };

    const handleSavePredefinedComments = useCallback(
      (options: PredefinedCommentOption[]) => {
        onOptionsChange(options, index);
      },
      [index, onOptionsChange],
    );

    useEffect(() => {
      setActiveSwitch(resultTypeToSwitch[resultType] || 1);
    }, [resultType]);

    useEffect(() => {}, [isOpen, inspectionItemId]);

    useEffect(() => {
      if (focusOnName && isOpen && nameInputRef.current) {
        nameInputRef.current.focus();
      }
    }, [focusOnName, isOpen]);

    const lastValidationRef = useRef<string | null>(null);

    useEffect(() => {
      if (lastValidationRef.current === prevCardDescription) return;
      lastValidationRef.current = prevCardDescription;

      const isValid =
        prevCardDescription.trim() === "" ||
        (REGEX.commonNameFieldCharacterMuiltiLineRegex.test(
          prevCardDescription,
        ) &&
          REGEX.emojiRegex.test(prevCardDescription));

      if (!isValid) {
        setDescriptionError(t("invalidCharacters"));
        onValidationChangeDesc(false, index);
      } else {
        setDescriptionError(null);
        onValidationChangeDesc(true, index);
      }
    }, [prevCardDescription, index, onValidationChangeDesc, t]);

    const lastNameValidationRef = useRef<string | null>(null);

    useEffect(() => {
      if (lastNameValidationRef.current === localName) return;
      lastNameValidationRef.current = localName;

      const trimmedName = localName.trim();
      const isNonEmpty = trimmedName !== "";
      const isValid =
        isNonEmpty &&
        REGEX.commonNameFieldRegex.test(trimmedName) &&
        REGEX.emojiRegex.test(trimmedName);

      if (!isValid) {
        if (!isNonEmpty) {
          setNameError(t("messages.require"));
        } else if (!REGEX.commonNameFieldRegex.test(trimmedName)) {
          setNameError(t("invalidCharacters"));
        } else if (!REGEX.emojiRegex.test(trimmedName)) {
          setNameError(t("emojisarenotallowed"));
        }
        onValidationChange(false, index);
      } else {
        setNameError(null);
        onValidationChange(true, index);
      }
    }, [localName, index, onValidationChange, t]);

    const didInitResultTypeRef = useRef(false);
    useEffect(() => {
      if (didInitResultTypeRef.current) return;
      didInitResultTypeRef.current = true;

      onResultTypeChange(
        switchToResultType[activeSwitch as keyof typeof switchToResultType],
        index,
      );
    }, [activeSwitch, index, onResultTypeChange]);

    useEffect(() => {
      const files = mediaFiles ?? [];
      setFileCount(files.length);
      setLocalMediaFiles([...files]);
    }, [mediaFiles]);

    return (
      <div
        ref={ref}
        className="card-item card-item-draggable bg-gray"
        style={{
          ...styles.cardContainer,
          minHeight: "auto",
          height: "auto",
          opacity: isDragging ? 0.5 : 1,
        }}
        data-handler-id={handlerId || undefined}
      >
        <div
          className="card-item-title flex"
          style={{ justifyContent: "space-between", alignItems: "center" }}
          onClick={toggleCard}
        >
          <div className="flex align-items-center" style={{ gap: "15px" }}>
            {showDragIcon && (
              <div
                ref={dragIconRef}
                className="wrapper-drag-icon flex align-items-center"
                style={{ cursor: "pointer" }}
              >
                <DragIndicator
                  className="drag-icon"
                  style={{ color: "#adaaaf" }}
                />
              </div>
            )}
            <span
              className="txt-card txt-pre-wrap word-break"
              style={styles.text}
            >
              {!inspectionItemNameErrors?.type
                ? inspectionItemName
                : prevCardName}
            </span>
          </div>

          <button
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              padding: 0,
              color: "#cdcdcd",
            }}
          >
            {isOpen ? (
              <KeyboardArrowUp className="arrow-icon ml-10" />
            ) : (
              <KeyboardArrowDown className="arrow-icon ml-10" />
            )}
          </button>
        </div>

        <div
          className="card-item-content"
          style={{
            height: isOpen ? "auto" : "0px",
            opacity: isOpen ? 1 : 0,
            overflow: isOpen ? "visible" : "hidden",
            transition: "opacity 0.3s ease",
          }}
        >
          {isOpen && (
            <div className="card-item-content d-block">
              <div
                className="wrapper-text-field mt-8"
                style={styles.textFieldWrapper}
              >
                <TextField
                  id={
                    inspectionItemNameErrors || nameError
                      ? "outlined-error"
                      : "outlined-required"
                  }
                  label={`${t("itemName")}*`}
                  className="input-field"
                  error={!!inspectionItemNameErrors || !!nameError}
                  value={localName}
                  onChange={(e) =>
                    handleNameChange(safeSliceGraphemes(e.target.value, 100))
                  }
                  onBlur={(e) => handleNameBlur(e)}
                  InputProps={{
                    endAdornment: (inspectionItemNameErrors || nameError) && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "100%",
                        }}
                      >
                        <IconInputError />
                      </div>
                    ),
                  }}
                  inputRef={nameInputRef}
                />
                {nameError ? (
                  <span className="error-text fs-12 ml-15 txt-red">
                    {nameError}
                  </span>
                ) : (
                  <span className="fs-12 ml-15 txt-gray-1">
                    &#42;{t("required")}
                  </span>
                )}
              </div>

              <div
                className="wrapper-text-field mt-8"
                style={styles.textFieldWrapper}
              >
                <TextField
                  multiline
                  maxRows={3}
                  className="input-field input-text-area"
                  id={
                    inspectionItemDesErrors || descriptionError
                      ? "outlined-error"
                      : "outlined-required"
                  }
                  label={t("explanation")}
                  error={!!inspectionItemDesErrors || !!descriptionError}
                  value={prevCardDescription}
                  onChange={(e) =>
                    handleDescriptionChange(
                      safeSliceGraphemes(e.target.value, 256),
                    )
                  }
                  onBlur={(e) => handleDescriptionBlur(e)}
                  InputProps={{
                    endAdornment: (inspectionItemDesErrors ||
                      descriptionError) && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "100%",
                        }}
                      >
                        <IconInputError />
                      </div>
                    ),
                  }}
                />
                {descriptionError ? (
                  <span className="error-text fs-12 ml-15 txt-red">
                    {descriptionError}
                  </span>
                ) : (
                  <span className="fs-12 ml-15 txt-gray-1">{t("")}</span>
                )}
              </div>

              <div className="mt-16">
                <MediaUpload
                  key={refreshKey}
                  setFileCount={setFileCount}
                  medias={localMediaFiles}
                  setMedias={(files: File[]) => {
                    handleMediaUpload(files);
                  }}
                  id={inspectionItemId}
                  typeFile={TYPE_FILE_UPLOAD.imageAndVideo}
                  uploadDescription={uploadDescription}
                />
              </div>

              <Accordion
                className="mt-10"
                sx={{
                  boxShadow: "none",
                  borderBottom: "1px solid #ddd",
                  borderTop: "1px solid #ddd",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1-content"
                  id="panel1-header"
                  className="card-bg"
                  sx={{ backgroundColor: "#f5f6f9", marginRight: "-22px" }}
                >
                  <Typography style={{ marginLeft: "-15px" }} component="span">
                    {t("AdvancedSettings")}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  className="card-bg"
                  sx={{ backgroundColor: "#f5f6f9" }}
                >
                  <div>
                    <p
                      className="txt-gray-3 mb-16"
                      style={{ marginLeft: "-15px" }}
                    >
                      {t("AnswerOptions")}
                    </p>
                    <div className="d-flex gap-24 justify-between flex-wrap answer-options-row">
                      {answerOptions.map((option) => (
                        <label
                          key={option.id}
                          className={`option-card ${
                            activeSwitch === option.id ? "selected" : ""
                          }`}
                        >
                          <div className="icons-row d-flex justify-content-center mb-8">
                            {option.icons?.map((Icon, idx) => (
                              <span key={idx} className="mx-5">
                                {Icon}
                              </span>
                            ))}
                          </div>
                          <div className="label text-center">
                            {option.label}
                          </div>
                          <input
                            type="radio"
                            name={`answerOption-${inspectionItemId}`}
                            checked={activeSwitch === option.id}
                            onChange={() => handleSwitchChange(option.id)}
                          />
                        </label>
                      ))}

                      <div
                        className={`option-card predefined-comment-option ${
                          activeSwitch === 4 ? "selected" : ""
                        }`}
                      >
                        <label className="predefined-comment-option-inner">
                          <div className="icons-row d-flex justify-content-center mb-8">
                            <IconMessage />
                          </div>
                          <div className="label text-center predefined-comment-option-label">
                            {t("predefinedCommentSelection")}
                          </div>
                          <input
                            type="radio"
                            name={`answerOption-${inspectionItemId}`}
                            checked={activeSwitch === 4}
                            onChange={() => handleSwitchChange(4)}
                          />
                        </label>

                        {activeSwitch === 4 && (
                          <button
                            type="button"
                            className={`predefined-comment-config-btn ${
                              hasPredefinedComments ? "has-items" : "no-items"
                            }`}
                            onClick={() => setOpenPredefinedDialog(true)}
                          >
                            <FormatListBulleted className="predefined-comment-config-icon" />
                            <span>{t("setPredefinedComments")}</span>
                            <span className="predefined-comment-config-count">
                              {t("predefinedCommentCount", {
                                count: predefinedCommentCount,
                                max: MAX_PREDEFINED_COMMENTS,
                              })}
                            </span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </AccordionDetails>
              </Accordion>

              <div
                className="d-flex justify-content-end mt-16"
                style={{ marginRight: "-10px", marginTop: "15px" }}
              >
                <Button
                  onClick={() => setIsOpenDeleteDialog(true)}
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                  style={{
                    border: `1px solid ${
                      isHovered ? "#F44336" : "transparent"
                    }`,
                    borderRadius: "100px",
                    backgroundColor: "transparent",
                    color: "#F44336",
                    padding: "6px 14px",
                    fontSize: "12px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    transition: "border 0.3s ease, background-color 0.3s ease",
                    minHeight: "32px",
                  }}
                >
                  <IconDelete />
                  <span
                    style={{
                      marginLeft: "10px",
                      fontSize: "14px",
                      color: "#F44336",
                    }}
                  >
                    {t("formAction.delete")}
                  </span>
                </Button>
              </div>
            </div>
          )}
        </div>
        <ConfirmDialog
          open={isOpenDeleteDialog}
          setOpen={setIsOpenDeleteDialog}
          {...confirmProps}
        />
        <PredefinedCommentsDialog
          open={openPredefinedDialog}
          setOpen={setOpenPredefinedDialog}
          initialOptions={itemOptions}
          onSave={handleSavePredefinedComments}
        />
      </div>
    );
  },
);
